// Fire-and-forget in-product usage tracker. Posts to the
// track-analytics-event Edge Function. Uses keepalive so a page-view/CTA
// beacon survives even if a same-tab navigation follows right after.
//
// Never throws — analytics must never break a UX flow. Mirrors the shape
// of landing/lib/track-cta.ts (same Edge Function pattern, different app).

export type CtaName =
  | "connect_wallet"
  | "get_more"
  | "redeem_open"
  | "redeem_confirm";

const ENDPOINT = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/track-analytics-event`;
const APIKEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

// Anonymous per-tab session id — lets the dashboard tell a new arrival
// apart from in-session navigation (the earliest page_view per session_id
// is that session's entry page). Lives in sessionStorage, so it's
// per-tab and gone when the tab closes — never a persistent visitor id,
// never PII.
const SESSION_STORAGE_KEY = "qinv_analytics_session_id";

// crypto.randomUUID() requires a secure context and isn't implemented in
// every environment this app can end up embedded in (older browsers,
// restrictive WebViews — this app is embedded as a Farcaster/Base miniapp,
// per the fc:miniapp/base:app_id tags in index.html, so unusual embedding
// contexts are a real possibility, not just a hypothetical). Never let a
// missing/throwing crypto.randomUUID reach the caller.
function randomId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
  }
}

function getSessionId(): string {
  try {
    const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;
    const id = randomId();
    try {
      window.sessionStorage.setItem(SESSION_STORAGE_KEY, id);
    } catch {
      // Write blocked (privacy mode, sandboxed iframe, etc.) — fine, the
      // id just won't persist across navigation in this tab.
    }
    return id;
  } catch {
    // Reading sessionStorage itself threw — fall back to a fresh id.
    // Breaks session grouping for this tab, but never throws, and every
    // event still gets a validly-shaped id.
    return randomId();
  }
}

// Mirrors normalizeRoute() in supabase/functions/track-analytics-event/index.ts.
// Keeps `route` cardinality bounded to a handful of buckets — never log a
// raw pathname verbatim (the server re-checks this too, but doing it here
// avoids sending junk over the wire in the first place).
const KNOWN_ROUTES = new Set(["/", "/whitepaper", "/portfolios", "/dashboard"]);
const PORTFOLIO_ROUTE_RE = /^\/portfolios\/[a-z0-9-]{1,40}$/i;

export function normalizeRoute(pathname: string): string {
  if (KNOWN_ROUTES.has(pathname)) return pathname;
  if (PORTFOLIO_ROUTE_RE.test(pathname)) return pathname;
  if (pathname.startsWith("/dashboard")) return "/dashboard";
  return "/other";
}

function send(payload: Record<string, unknown>): void {
  if (!ENDPOINT || !APIKEY) return;

  try {
    void fetch(ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: APIKEY,
      },
      body: JSON.stringify(payload),
      keepalive: true,
      // Don't block the caller. We don't care about the response.
    }).catch(() => {});
  } catch {
    // Silent — never disturb the user.
  }
}

// Both exports are the public surface callers use directly from click
// handlers, ahead of the real action (see e.g. RedemptionModal.tsx) — they
// must be able to NEVER throw, regardless of what fails internally
// (sessionStorage, crypto.randomUUID, fetch, JSON, anything). A tracking
// failure must never prevent the real button action from running.
export function trackPageView(pathname: string): void {
  try {
    send({ session_id: getSessionId(), type: "page_view", route: normalizeRoute(pathname) });
  } catch {
    // Never let tracking break navigation.
  }
}

export function trackCtaClick(name: CtaName): void {
  try {
    send({ session_id: getSessionId(), type: "cta_click", cta_name: name });
  } catch {
    // Never let tracking break the actual CTA action.
  }
}
